import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageHero from "@/components/PageHero";
import api, { formatApiError } from "@/lib/api";
import { INDIAN_STATES, MEMBERSHIP_CATEGORIES } from "@/lib/brand";
import { CheckCircle2, AlertCircle, FileText } from "lucide-react";

const DISCIPLINES = [
  ["traditional", "Traditional Karate"],
  ["semi_contact", "Semi Contact Karate"],
  ["full_contact", "Full Contact Karate"],
];

export default function Register() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    category: params.get("cat") || "active_youth",
    applicant_name: "",
    organisation_name: "",
    email: "",
    phone: "",
    state: "",
    district: "",
    address: "",
    discipline: "traditional",
    dob: "",
    gender: "M",
    belt_rank: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/memberships", form);
      setSuccess(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div data-testid="register-success" className="min-h-[60vh]">
        <PageHero eyebrow="Application Submitted" title="Welcome to NKF Bharat" />
        <section className="nkf-container py-12">
          <div className="max-w-2xl mx-auto bg-white border border-slate-200 rounded-md p-8 text-center">
            <CheckCircle2 className="h-14 w-14 text-emerald-600 mx-auto" />
            <h2 className="font-display text-2xl text-nkf-blue mt-4">Application received</h2>
            <p className="text-slate-600 mt-2">Your federation application is now in the review queue.</p>
            <div className="mt-5 inline-block bg-nkf-gold/15 border border-nkf-gold/40 px-4 py-3 rounded">
              <div className="text-[11px] uppercase tracking-widest text-nkf-blue">Application Number</div>
              <div className="font-display text-2xl text-nkf-blue mt-1" data-testid="application-number">{success.application_no}</div>
            </div>
            <p className="text-xs text-slate-500 mt-4">Save this number. Our secretariat will contact you with payment & verification instructions.</p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="register-page">
      <PageHero
        eyebrow="Registration"
        title="Federation Membership Application"
        subtitle="Complete the form below to register with the National Karate Federation – NKF Bharat. Payment processing is handled offline by the federation office after verification."
      />

      <section className="section-spacing nkf-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <aside className="lg:col-span-4">
            <div className="bg-slate-50 border border-slate-200 rounded-md p-5 sticky top-32">
              <div className="text-[11px] uppercase tracking-[0.3em] text-nkf-red font-semibold">Select Category</div>
              <ul className="mt-3 space-y-1.5">
                {MEMBERSHIP_CATEGORIES.map((c) => (
                  <li key={c.key}>
                    <button
                      type="button"
                      onClick={() => set("category", c.key)}
                      data-testid={`category-select-${c.key}`}
                      className={`w-full text-left px-3 py-2 rounded text-sm border transition ${
                        form.category === c.key
                          ? "bg-nkf-blue text-white border-nkf-blue"
                          : "bg-white border-slate-200 hover:border-nkf-blue/40 text-slate-700"
                      }`}
                    >
                      {c.title}
                    </button>
                  </li>
                ))}
              </ul>
              <div className="mt-5 p-3 bg-white border border-slate-200 rounded text-xs text-slate-500 flex items-start gap-2">
                <FileText className="h-3.5 w-3.5 mt-0.5 text-nkf-blue" />
                Required documents will be requested by the federation post-submission via email.
              </div>
            </div>
          </aside>

          <form onSubmit={onSubmit} data-testid="register-form" className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-4 bg-white border border-slate-200 rounded-md p-6">
            {error && (
              <div className="md:col-span-2 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 p-3 rounded text-sm">
                <AlertCircle className="h-4 w-4 mt-0.5" /> {error}
              </div>
            )}
            <Field label="Applicant / Contact Name *" testid="reg-applicant-name">
              <input required value={form.applicant_name} onChange={(e) => set("applicant_name", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Organisation Name (if any)" testid="reg-organisation-name">
              <input value={form.organisation_name} onChange={(e) => set("organisation_name", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Email *" testid="reg-email">
              <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Phone *" testid="reg-phone">
              <input required value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} />
            </Field>
            <Field label="State *" testid="reg-state">
              <select required value={form.state} onChange={(e) => set("state", e.target.value)} className={inputCls}>
                <option value="">Select State</option>
                {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="District *" testid="reg-district">
              <input required value={form.district} onChange={(e) => set("district", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Discipline" testid="reg-discipline">
              <select value={form.discipline} onChange={(e) => set("discipline", e.target.value)} className={inputCls}>
                {DISCIPLINES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <Field label="Date of Birth" testid="reg-dob">
              <input type="date" value={form.dob} onChange={(e) => set("dob", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Gender" testid="reg-gender">
              <select value={form.gender} onChange={(e) => set("gender", e.target.value)} className={inputCls}>
                <option value="M">Male</option>
                <option value="F">Female</option>
                <option value="O">Other</option>
              </select>
            </Field>
            <Field label="Current Belt Rank" testid="reg-belt">
              <input value={form.belt_rank} onChange={(e) => set("belt_rank", e.target.value)} className={inputCls} placeholder="e.g. Black Belt 1st Dan" />
            </Field>
            <Field label="Address" testid="reg-address" wide>
              <input value={form.address} onChange={(e) => set("address", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Notes / Achievements" testid="reg-notes" wide>
              <textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} className={inputCls} />
            </Field>
            <div className="md:col-span-2 flex items-center justify-between mt-2">
              <div className="text-xs text-slate-500">
                Selected category: <span className="font-semibold text-nkf-blue">{MEMBERSHIP_CATEGORIES.find((c) => c.key === form.category)?.title}</span>
              </div>
              <button
                disabled={submitting}
                data-testid="submit-application"
                className="bg-nkf-blue text-white uppercase tracking-widest text-sm font-bold px-6 py-3 rounded hover:bg-nkf-navy disabled:opacity-60 transition"
              >
                {submitting ? "Submitting…" : "Submit Application"}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

const inputCls = "w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-nkf-blue/30 focus:border-nkf-blue";

function Field({ label, children, wide, testid }) {
  return (
    <label className={`block ${wide ? "md:col-span-2" : ""}`} data-testid={testid}>
      <span className="block text-[12px] uppercase tracking-widest text-slate-600 mb-1.5 font-semibold">{label}</span>
      {children}
    </label>
  );
}
